import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedImmobile, RedScadenza } from '../../../types/red'
import { redApi } from '../../../services/api'

const STATO_LABEL: Record<RedScadenza['stato'], string> = {
  attiva: 'Attiva',
  in_pausa: 'In pausa',
  confermata: 'Confermata',
}

const TabScadenzario: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile, onUpdate }) => {
  const [scadenze, setScadenze] = useState<RedScadenza[]>([])
  const [loading, setLoading] = useState(true)
  const [editCadenzaId, setEditCadenzaId] = useState<string | null>(null)
  const [editGiorni, setEditGiorni] = useState('')
  const [posticipaId, setPosticipaId] = useState<string | null>(null)
  const [posticipaData, setPosticipaData] = useState('')

  const load = () => {
    redApi.admin.listScadenze(immobile.id).then(res => {
      setScadenze(res.data as RedScadenza[])
      setLoading(false)
    }).catch(() => {
      setLoading(false)
      toast.error('Errore caricamento scadenzario')
    })
  }

  useEffect(load, [immobile.id])

  const aggiorna = async (id: string, data: Parameters<typeof redApi.admin.updateScadenza>[1], msgSuccesso: string) => {
    try {
      await redApi.admin.updateScadenza(id, data)
      toast.success(msgSuccesso)
      load()
      onUpdate()
    } catch {
      toast.error('Errore aggiornamento scadenza')
    }
  }

  const handleElimina = async (id: string) => {
    if (!confirm('Eliminare questa scadenza?')) return
    try {
      await redApi.admin.deleteScadenza(id)
      toast.success('Scadenza eliminata')
      load()
      onUpdate()
    } catch {
      toast.error('Errore eliminazione')
    }
  }

  const handleSalvaCadenza = async (id: string) => {
    const giorni = editGiorni.split(',').map(g => parseInt(g.trim(), 10)).filter(g => !isNaN(g) && g >= 0)
    if (giorni.length === 0) {
      toast.error('Inserisci almeno un giorno di preavviso valido')
      return
    }
    await aggiorna(id, { giorni_preavviso: giorni }, 'Cadenza aggiornata')
    setEditCadenzaId(null)
  }

  const handleSalvaPosticipa = async (id: string) => {
    if (!posticipaData) {
      toast.error('Seleziona una data')
      return
    }
    await aggiorna(id, { sospesa_fino: posticipaData }, 'Scadenza posticipata: avvisi ripresi automaticamente dopo la data scelta')
    setPosticipaId(null)
    setPosticipaData('')
  }

  if (loading) return <div className="text-gray-400">Caricamento scadenzario...</div>

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Scadenzario</h2>
      <div className="space-y-2">
        {scadenze.map(s => {
          const giorni = Math.ceil((new Date(s.data_scadenza).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
          const scaduta = giorni < 0
          return (
            <div key={s.id} className={`p-3 rounded-lg border ${scaduta && s.stato === 'attiva' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-800">{s.titolo}</div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    s.stato === 'confermata' ? 'bg-green-50 text-green-700' :
                    s.stato === 'in_pausa' ? 'bg-gray-200 text-gray-600' : 'bg-blue-50 text-blue-700'
                  }`}>
                    {STATO_LABEL[s.stato]}
                  </span>
                  <div className={`text-xs font-semibold ${scaduta ? 'text-red-600' : 'text-gray-500'}`}>
                    {scaduta ? `SCADUTA da ${Math.abs(giorni)}gg` : `tra ${giorni}gg`}
                    {' · '}
                    {new Date(s.data_scadenza).toLocaleDateString('it-IT')}
                  </div>
                  <button onClick={() => handleElimina(s.id)} className="text-xs text-red-500 hover:text-red-700" title="Elimina">🗑</button>
                </div>
              </div>

              {s.sospesa_fino && s.stato === 'attiva' && (
                <div className="text-xs text-amber-600 mt-1">Avvisi sospesi fino al {new Date(s.sospesa_fino).toLocaleDateString('it-IT')}</div>
              )}
              {s.confermata_da && (
                <div className="text-xs text-gray-400 mt-1">Confermata da: {s.confermata_da}</div>
              )}

              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                {s.stato !== 'in_pausa' && (
                  <button onClick={() => aggiorna(s.id, { stato: 'in_pausa' }, 'Avvisi sospesi (Stop)')} className="text-gray-600 hover:underline">Stop</button>
                )}
                {s.stato === 'in_pausa' && (
                  <button onClick={() => aggiorna(s.id, { stato: 'attiva' }, 'Scadenza riattivata')} className="text-blue-600 hover:underline">Riattiva</button>
                )}
                {s.stato !== 'confermata' && (
                  <button onClick={() => aggiorna(s.id, { stato: 'confermata' }, 'Scadenza confermata: nessun altro avviso')} className="text-green-600 hover:underline">Conferma manuale</button>
                )}
                {s.stato === 'attiva' && (
                  posticipaId === s.id ? (
                    <span className="flex items-center gap-1">
                      <input type="date" className="input text-xs py-0.5" value={posticipaData} onChange={e => setPosticipaData(e.target.value)} />
                      <button onClick={() => handleSalvaPosticipa(s.id)} className="text-blue-600 hover:underline">Salva</button>
                      <button onClick={() => setPosticipaId(null)} className="text-gray-400 hover:underline">Annulla</button>
                    </span>
                  ) : (
                    <button onClick={() => { setPosticipaId(s.id); setPosticipaData('') }} className="text-gray-600 hover:underline">Posticipa</button>
                  )
                )}
                {editCadenzaId === s.id ? (
                  <span className="flex items-center gap-1">
                    <input className="input text-xs py-0.5 w-28" value={editGiorni} onChange={e => setEditGiorni(e.target.value)} placeholder="30, 7, 1" />
                    <button onClick={() => handleSalvaCadenza(s.id)} className="text-blue-600 hover:underline">Salva</button>
                    <button onClick={() => setEditCadenzaId(null)} className="text-gray-400 hover:underline">Annulla</button>
                  </span>
                ) : (
                  <button onClick={() => { setEditCadenzaId(s.id); setEditGiorni(s.giorni_preavviso.join(', ')) }} className="text-gray-600 hover:underline">
                    Cadenza: {s.giorni_preavviso.join(', ')}gg
                  </button>
                )}
              </div>
            </div>
          )
        })}
        {scadenze.length === 0 && (
          <div className="text-center py-10 text-gray-400">Nessuna scadenza per questo immobile.</div>
        )}
      </div>
    </div>
  )
}

export default TabScadenzario
